import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const panelStyle = {
  margin: '0 0 20px',
  padding: 18,
  border: '1px solid #cbd8e6',
  borderRadius: 10,
  background: '#f8fafc',
  color: '#26364d',
};
const fieldStyle = {
  width: '100%',
  minHeight: 40,
  boxSizing: 'border-box',
  border: '1px solid #c8d4e4',
  borderRadius: 7,
  padding: '9px 11px',
  font: 'inherit',
  color: '#23344d',
  background: '#fff',
};
const buttonStyle = {
  border: '1px solid #1b3a6b',
  borderRadius: 7,
  padding: '9px 13px',
  color: '#fff',
  background: '#1b3a6b',
  fontWeight: 700,
  cursor: 'pointer',
};
const quietButtonStyle = {
  ...buttonStyle,
  color: '#33445d',
  background: '#fff',
  borderColor: '#c8d4e4',
};
const alertStyle = {
  padding: '10px 12px',
  marginTop: 12,
  border: '1px solid #e8b4a8',
  borderRadius: 7,
  color: '#8a372b',
  background: '#fff3f0',
  fontSize: 13,
};

function formatDate(value) {
  if (!value) return 'Not recorded';
  const date = new Date(`${value}`.slice(0, 10) + (String(value).length <= 10 ? 'T12:00:00' : ''));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

async function parseJsonResponse(response, fallback) {
  if (!response.headers.get('content-type')?.includes('application/json')) {
    throw new Error('The contract service returned an unexpected response. Your form is still here; retry shortly.');
  }
  const payload = await response.json();
  if (!response.ok) throw new Error(payload.error || payload.message || fallback);
  return payload;
}

export default function ExistingSignedContracts({ job, token, onContractsChange }) {
  const jobId = job?.id;
  const authHeaders = useMemo(() => (token ? { 'x-auth-token': token } : {}), [token]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(Boolean(jobId));
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [contractNumber, setContractNumber] = useState('');
  const [title, setTitle] = useState('');
  const [signedOn, setSignedOn] = useState('');
  const [file, setFile] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busyFileId, setBusyFileId] = useState(null);
  const [previews, setPreviews] = useState({});
  const urlsRef = useRef([]);
  const fileInputRef = useRef(null);
  const sequenceRef = useRef(0);

  const loadContracts = useCallback(async () => {
    const sequence = ++sequenceRef.current;
    if (!jobId) {
      setContracts([]);
      setLoading(false);
      onContractsChange?.([]);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const response = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/existing-contracts`, {
        headers: authHeaders,
      });
      const payload = await parseJsonResponse(response, 'Unable to load imported contracts.');
      if (sequence !== sequenceRef.current) return;
      const nextContracts = Array.isArray(payload.contracts) ? payload.contracts : [];
      setContracts(nextContracts);
      onContractsChange?.(nextContracts);
    } catch (error) {
      if (sequence === sequenceRef.current) setLoadError(error.message || 'Unable to load imported contracts.');
    } finally {
      if (sequence === sequenceRef.current) setLoading(false);
    }
  }, [jobId, authHeaders, onContractsChange]);

  useEffect(() => {
    loadContracts();
    return () => {
      sequenceRef.current += 1;
    };
  }, [loadContracts]);

  useEffect(
    () => () => {
      urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    },
    [],
  );

  const submitImport = async (event) => {
    event.preventDefault();
    setActionError('');
    if (!contractNumber.trim() || contractNumber.trim().length > 80) {
      setActionError('Enter the original contract reference (up to 80 characters).');
      return;
    }
    if (!title.trim() || title.trim().length > 160) {
      setActionError('Enter a contract title (up to 160 characters).');
      return;
    }
    if (!file || file.type !== 'application/pdf') {
      setActionError('Choose a PDF file for the already signed contract.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setActionError('The contract PDF must be 10 MB or smaller.');
      return;
    }
    if (!confirmed) {
      setActionError('Confirm that this contract is already signed and belongs to this customer and job.');
      return;
    }

    const formData = new FormData();
    formData.append('contract_number', contractNumber.trim());
    formData.append('title', title.trim());
    if (signedOn) formData.append('signed_on', signedOn);
    formData.append('file', file, file.name);
    formData.append('confirm_existing', 'true');
    setUploading(true);
    try {
      const response = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/existing-contracts`, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
      });
      await parseJsonResponse(response, 'Unable to import the signed contract.');
      setContractNumber('');
      setTitle('');
      setSignedOn('');
      setFile(null);
      setConfirmed(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadContracts();
    } catch (error) {
      setActionError(error.message || 'Unable to import the signed contract. Your input has been kept.');
    } finally {
      setUploading(false);
    }
  };

  const fetchFile = async (contract, original = false) => {
    setActionError('');
    setBusyFileId(contract.id);
    try {
      const suffix = original ? '?original=1' : '';
      const response = await fetch(
        `/api/jobs/${encodeURIComponent(jobId)}/existing-contracts/${encodeURIComponent(contract.id)}/file${suffix}`,
        { headers: authHeaders },
      );
      if (!response.ok) {
        let message = 'Unable to retrieve the imported contract PDF.';
        if (response.headers.get('content-type')?.includes('application/json')) {
          const payload = await response.json().catch(() => ({}));
          message = payload.error || payload.message || message;
        } else {
          message = 'The contract service returned an unexpected response. Retry to load the PDF.';
        }
        throw new Error(message);
      }
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/pdf')) {
        throw new Error('The contract service did not return a PDF. Retry or contact support.');
      }
      const url = URL.createObjectURL(await response.blob());
      urlsRef.current.push(url);
      if (original) {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${(contract.contract_number || 'signed-contract').replace(/[^a-z0-9-_]+/gi, '_')}_original.pdf`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        return;
      }
      setPreviews((previous) => ({
        ...previous,
        [contract.id]: {
          url,
          name: `${(contract.contract_number || contract.title || 'signed-contract').replace(/[^a-z0-9-_]+/gi, '_')}.pdf`,
        },
      }));
    } catch (error) {
      setActionError(error.message || 'Unable to retrieve the imported contract PDF.');
    } finally {
      setBusyFileId(null);
    }
  };


  const customer = job?.customer_name || 'Customer not listed';
  const address =
    [job?.project_address, job?.project_city, job?.project_state].filter(Boolean).join(', ') ||
    'Address not listed';

  return (
    <section style={panelStyle} aria-labelledby="existing-signed-contracts-title">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 id="existing-signed-contracts-title" style={{ margin: 0, color: '#1b3a6b', fontSize: 17 }}>
            Import an already signed contract
          </h3>
          <p style={{ margin: '5px 0 0', color: '#627188', fontSize: 13 }}>
            For agreements signed outside this app. This is separate from Create Proposal.
          </p>
        </div>
        <button type="button" onClick={loadContracts} disabled={loading} style={quietButtonStyle}>
          {loading ? 'Refreshing…' : 'Refresh imports'}
        </button>
      </div>

      <div
        style={{
          marginTop: 12,
          padding: 12,
          borderLeft: '3px solid #b8742a',
          borderRadius: 5,
          background: '#fff8ed',
          color: '#674821',
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        <strong>Current job:</strong> {customer} · {address}
        <div style={{ marginTop: 4 }}>
          Import only stores the signed document and reference. It does not create a proposal,
          signature request, deposit invoice, or payment, and does not change the job total or status.
          Nothing is sent automatically.
        </div>
      </div>

      {actionError && <div role="alert" style={alertStyle}>{actionError}</div>}
      {loadError && (
        <div role="alert" style={alertStyle}>
          {loadError}{' '}
          <button type="button" onClick={loadContracts} style={{ ...quietButtonStyle, padding: '4px 8px' }}>
            Retry
          </button>
        </div>
      )}

      <form onSubmit={submitImport} style={{ display: 'grid', gap: 11, marginTop: 15 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 11 }}>
          <label style={{ display: 'grid', gap: 5, color: '#43546c', fontSize: 12, fontWeight: 700 }}>
            Original contract reference <span style={{ color: '#8a372b' }}>Required</span>
            <input
              required
              maxLength={80}
              value={contractNumber}
              onChange={(event) => setContractNumber(event.target.value)}
              style={fieldStyle}
              autoComplete="off"
            />
          </label>
          <label style={{ display: 'grid', gap: 5, color: '#43546c', fontSize: 12, fontWeight: 700 }}>
            Contract title <span style={{ color: '#8a372b' }}>Required</span>
            <input
              required
              maxLength={160}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              style={fieldStyle}
            />
          </label>
          <label style={{ display: 'grid', gap: 5, color: '#43546c', fontSize: 12, fontWeight: 700 }}>
            Signed on <span style={{ color: '#718096', fontWeight: 400 }}>Optional</span>
            <input type="date" value={signedOn} onChange={(event) => setSignedOn(event.target.value)} style={fieldStyle} />
          </label>
        </div>
        <label style={{ display: 'grid', gap: 5, color: '#43546c', fontSize: 12, fontWeight: 700 }}>
          Signed contract PDF <span style={{ color: '#718096', fontWeight: 400 }}>PDF · maximum 10 MB</span>
          <input
            ref={fileInputRef}
            required
            type="file"
            accept="application/pdf,.pdf"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
            style={{ ...fieldStyle, padding: 7 }}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, color: '#34445b', fontSize: 13, lineHeight: 1.5 }}>
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            I confirm this contract is already signed and belongs to <strong>{customer}</strong> at{' '}
            <strong>{address}</strong> for this displayed job.
          </span>
        </label>
        <div>
          <button type="submit" disabled={uploading || !jobId} style={buttonStyle}>
            {uploading ? 'Importing…' : 'Import signed contract'}
          </button>
        </div>
      </form>

      <div style={{ marginTop: 20 }}>
        <h4 style={{ margin: '0 0 9px', color: '#26364d', fontSize: 14 }}>Imported contracts</h4>
        {loading && !contracts.length ? (
          <div aria-label="Loading imported contracts" style={{ display: 'grid', gap: 7 }}>
            {[0, 1].map((key) => (
              <div key={key} style={{ height: 48, borderRadius: 7, background: 'linear-gradient(90deg,#edf1f7,#f7f9fc,#edf1f7)' }} />
            ))}
          </div>
        ) : !contracts.length ? (
          <div style={{ padding: 13, border: '1px dashed #c5d1e0', borderRadius: 8, color: '#6b778a', fontSize: 13 }}>
            No previously signed contracts have been imported for this job.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 9 }}>
            {contracts.map((contract) => (
              <article key={contract.id} style={{ padding: 12, border: '1px solid #dbe3ef', borderRadius: 8, background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div>
                    <strong style={{ color: '#253650' }}>{contract.title}</strong>
                    <div style={{ marginTop: 4, color: '#627188', fontSize: 12 }}>
                      Reference {contract.contract_number} · Signed {formatDate(contract.signed_on)} · {contract.page_count ?? '—'} pages
                    </div>
                    <div style={{ marginTop: 3, color: '#718096', fontSize: 12 }}>
                      {contract.customer_name || customer} · {contract.project_address || address}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 7, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => fetchFile(contract)} disabled={busyFileId === contract.id} style={quietButtonStyle}>
                      {busyFileId === contract.id ? 'Loading PDF…' : previews[contract.id] ? 'Reload PDF' : 'Preview PDF'}
                    </button>
                    <button type="button" onClick={() => fetchFile(contract, true)} disabled={busyFileId === contract.id} style={quietButtonStyle}>
                      Download original
                    </button>
                  </div>
                </div>
                {previews[contract.id] && (
                  <iframe
                    title={`Imported signed contract ${contract.contract_number} PDF`}
                    src={previews[contract.id].url}
                    style={{ display: 'block', width: '100%', height: 420, marginTop: 11, border: '1px solid #dbe3ef', borderRadius: 7 }}
                  />
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
